/**
 * TrustPackVerifier — client-side tamper check for /trust.
 *
 * What it does (all in the browser, zero server cost):
 *   1. Re-fetches every source markdown from `/api/trust/docs/{slug}/download`.
 *   2. Hashes each one with Web Crypto SHA-256.
 *   3. Compares each hash to `manifest.docs[i].sha256` (per-doc tamper check).
 *   4. Recomputes the bundle SHA-256 the same way the backend does
 *      (`sha256("slug=hash\nslug=hash\n…")`) and compares to
 *      `manifest.bundle_sha256` — proves the manifest itself is authentic.
 *   5. Optional: drag in your previously-downloaded PDF → checks its
 *      byte-length against `manifest.size_bytes` for a quick sanity ping.
 *
 * Why two verifications?
 *   The bundle SHA on the page is the hash of the *manifest*, not the
 *   PDF. So a naive "hash the PDF, compare" doesn't work and would mislead
 *   procurement. The proper chain-of-custody check is:
 *      source markdowns  →  manifest  →  bundle SHA  →  the page pill.
 *   Each link is verifiable, locally, with no server in the loop.
 *
 * No server cost. No bandwidth cost beyond the 8 .md downloads
 * (which were already public). Pure Web Crypto.
 */
import React, { useState } from 'react';
import { ShieldCheck, AlertTriangle, Loader2, FileSearch, Upload } from 'lucide-react';
import ExplainThis from './ExplainThis';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

async function sha256Hex(buffer) {
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export default function TrustPackVerifier({ manifest, isDark }) {
  // null = not run, 'running', 'pass', 'fail'
  const [verifyState, setVerifyState] = useState(null);
  const [perDoc, setPerDoc] = useState([]); // [{ slug, title, ok, expected, actual }]
  const [bundleCheck, setBundleCheck] = useState(null); // { ok, expected, actual }
  const [pdfCheck, setPdfCheck] = useState(null); // { ok, expectedBytes, actualBytes, sha256 }
  const [pdfBusy, setPdfBusy] = useState(false);

  const open = !!manifest && verifyState !== null;

  const runVerification = async () => {
    if (!manifest) return;
    setVerifyState('running');
    setPerDoc([]);
    setBundleCheck(null);

    const results = [];
    for (const doc of manifest.docs || []) {
      try {
        const r = await fetch(`${API_BASE}/trust/docs/${doc.slug}/download`);
        if (!r.ok) {
          results.push({ ...doc, ok: false, actual: '(fetch failed)' });
          continue;
        }
        const buf = await r.arrayBuffer();
        const actual = await sha256Hex(buf);
        results.push({ ...doc, ok: actual === doc.sha256, actual, expected: doc.sha256 });
      } catch (err) {
        console.error('TrustPackVerifier: failed to verify', doc.slug, err);
        results.push({ ...doc, ok: false, actual: '(error)' });
      }
      // Incrementally render so user sees progress on slow connections.
      setPerDoc([...results]);
    }

    // Bundle SHA = sha256("slug=hash\nslug=hash\n…") over per-doc SHAs in
    // the *manifest's* declared order. Mirrors trust_pack_service.py.
    const bundleSrc = (manifest.docs || [])
      .map((d) => `${d.slug}=${d.sha256}`)
      .join('\n');
    const bundleActual = await sha256Hex(new TextEncoder().encode(bundleSrc));
    const bundleOk = bundleActual === manifest.bundle_sha256;
    setBundleCheck({ ok: bundleOk, expected: manifest.bundle_sha256, actual: bundleActual });

    const allDocsOk = results.every((r) => r.ok);
    setVerifyState(allDocsOk && bundleOk ? 'pass' : 'fail');
  };

  const onPdfDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (!file) return;
    setPdfBusy(true);
    try {
      const buf = await file.arrayBuffer();
      const sha = await sha256Hex(buf);
      const ok = buf.byteLength === manifest.size_bytes;
      setPdfCheck({ ok, expectedBytes: manifest.size_bytes, actualBytes: buf.byteLength, sha256: sha });
    } catch (err) {
      console.error('TrustPackVerifier: PDF hash failed', err);
      setPdfCheck({ ok: false, error: String(err) });
    } finally {
      setPdfBusy(false);
    }
  };

  if (!manifest) return null;

  const cardCls = isDark
    ? 'border-white/10 bg-white/[0.02]'
    : 'border-slate-200 bg-white';
  const subtle = isDark ? 'text-white/55' : 'text-slate-500';
  const mono = 'font-mono text-[10px] break-all';

  return (
    <div
      className={`mt-6 rounded-xl border ${cardCls}`}
      data-testid="trust-pack-verifier"
    >
      <button
        type="button"
        data-testid="trust-pack-verifier-toggle"
        onClick={() => {
          if (verifyState === null) {
            setVerifyState('idle');
          } else if (verifyState === 'idle') {
            setVerifyState(null);
          }
        }}
        className={`w-full flex items-center gap-3 p-4 text-left ${isDark ? 'hover:bg-white/[0.04]' : 'hover:bg-slate-50'} rounded-xl transition-colors`}
      >
        <div
          className="h-9 w-9 rounded-md flex items-center justify-center border shrink-0"
          style={{
            backgroundColor: 'rgba(174,82,234,0.10)',
            color: 'var(--ve-violet-bright)',
            borderColor: 'rgba(174,82,234,0.30)',
          }}
        >
          <FileSearch size={16} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: '#D4AF37' }}>
            [SELF-SERVICE TAMPER CHECK]
          </div>
          <div className={`text-sm font-bold mt-0.5 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Verify the pack — entirely in your browser, no auditor tooling.
          </div>
          <div className={`text-[11px] mt-0.5 ${subtle}`}>
            Re-fetches every source doc, recomputes SHA-256 via Web Crypto, and
            re-derives the bundle hash. Nothing leaves your device.
          </div>
        </div>
        <span
          className={`shrink-0 text-[10px] uppercase tracking-[0.22em] font-bold ${
            isDark ? 'text-white/55' : 'text-slate-500'
          }`}
        >
          {open ? 'Hide' : 'Verify'}
        </span>
      </button>

      {open && (
        <div className={`p-5 pt-2 border-t ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              data-testid="trust-pack-verifier-run"
              onClick={runVerification}
              disabled={verifyState === 'running'}
              className="inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.22em] font-bold rounded-lg bg-[var(--ve-violet-bright)] text-white hover:bg-[#7C2BAB] transition-colors disabled:opacity-60"
            >
              {verifyState === 'running' ? (
                <>
                  <Loader2 size={13} className="animate-spin" />
                  Verifying…
                </>
              ) : (
                <>
                  <ShieldCheck size={13} />
                  Run verification
                </>
              )}
            </button>

            {verifyState === 'pass' && (
              <span
                data-testid="trust-pack-verdict-pass"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold rounded border border-emerald-500/40 text-emerald-300 bg-emerald-500/[0.08]"
              >
                <ShieldCheck size={11} />
                Manifest verified
              </span>
            )}
            {verifyState === 'fail' && (
              <span
                data-testid="trust-pack-verdict-fail"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold rounded border border-rose-500/40 text-rose-300 bg-rose-500/[0.08]"
              >
                <AlertTriangle size={11} />
                Mismatch — see rows
              </span>
            )}
          </div>

          {/* Per-doc result rows */}
          {perDoc.length > 0 && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
              <div className={`grid grid-cols-[24px_1fr_auto] gap-2 px-3 py-2 text-[9px] uppercase tracking-[0.22em] font-bold ${isDark ? 'bg-white/[0.04] text-white/55' : 'bg-slate-50 text-slate-500'}`}>
                <span>✓</span>
                <span>Document</span>
                <span>SHA-256 (in-browser)</span>
              </div>
              {perDoc.map((d) => (
                <div
                  key={d.slug}
                  data-testid={`trust-pack-verify-${d.slug}`}
                  className={`grid grid-cols-[24px_1fr_auto] gap-2 px-3 py-2 items-center border-t ${isDark ? 'border-white/5' : 'border-slate-100'}`}
                >
                  <span className={d.ok ? 'text-emerald-400' : 'text-rose-400'} aria-hidden="true">
                    {d.ok ? '✓' : '✗'}
                  </span>
                  <span className="text-[11px] font-bold truncate">{d.title}</span>
                  <span className={`${mono} ${d.ok ? subtle : 'text-rose-400'}`} title={`expected: ${d.expected || '—'}\nactual:   ${d.actual}`}>
                    {(d.actual || '').slice(0, 16)}…
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Bundle SHA roll-up */}
          {bundleCheck && (
            <div
              data-testid="trust-pack-bundle-check"
              className={`mt-3 rounded-lg border p-3 ${
                bundleCheck.ok
                  ? 'border-emerald-500/30 bg-emerald-500/[0.06]'
                  : 'border-rose-500/40 bg-rose-500/[0.06]'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={bundleCheck.ok ? 'text-emerald-300' : 'text-rose-300'}>
                  {bundleCheck.ok ? <ShieldCheck size={14} /> : <AlertTriangle size={14} />}
                </span>
                <span className={`text-[11px] uppercase tracking-[0.22em] font-bold ${bundleCheck.ok ? 'text-emerald-300' : 'text-rose-300'}`}>
                  {bundleCheck.ok ? 'Bundle SHA matches the page pill' : 'Bundle SHA mismatch'}
                  <ExplainThis topic="bundle-sha" />
                </span>
              </div>
              <div className={`mt-1.5 ${mono} ${subtle}`}>
                expected&nbsp;<span className={isDark ? 'text-white/75' : 'text-slate-700'}>{bundleCheck.expected}</span>
              </div>
              <div className={`${mono} ${subtle}`}>
                derived&nbsp;&nbsp;<span className={bundleCheck.ok ? 'text-emerald-300' : 'text-rose-300'}>{bundleCheck.actual}</span>
              </div>
            </div>
          )}

          {/* Optional: drop a saved PDF to sanity-check its size */}
          <label
            htmlFor="trust-pack-pdf-drop"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onPdfDrop}
            className={`mt-4 flex items-center justify-between gap-3 p-3 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
              isDark
                ? 'border-white/15 hover:border-[var(--ve-violet-bright)]/60 hover:bg-white/[0.02]'
                : 'border-slate-300 hover:border-[var(--ve-violet-bright)] hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <Upload size={16} className="opacity-60" />
              <div className="min-w-0">
                <div className={`text-[11px] font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Optional: drop your saved PDF to size-check it
                </div>
                <div className={`text-[10px] ${subtle}`}>
                  Compares bytes against <span className={mono}>{manifest.size_bytes.toLocaleString()}</span> from the manifest. Local only.
                </div>
              </div>
            </div>
            <input
              id="trust-pack-pdf-drop"
              data-testid="trust-pack-pdf-input"
              type="file"
              accept="application/pdf,.pdf"
              className="sr-only"
              onChange={onPdfDrop}
            />
          </label>

          {pdfBusy && (
            <div className={`mt-2 text-[11px] ${subtle} flex items-center gap-2`}>
              <Loader2 size={12} className="animate-spin" /> Hashing in browser…
            </div>
          )}
          {pdfCheck && !pdfBusy && (
            <div
              data-testid="trust-pack-pdf-check"
              className={`mt-2 rounded-lg border p-3 ${
                pdfCheck.ok
                  ? 'border-emerald-500/30 bg-emerald-500/[0.06]'
                  : 'border-amber-500/30 bg-amber-500/[0.06]'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={pdfCheck.ok ? 'text-emerald-300' : 'text-amber-300'}>
                  {pdfCheck.ok ? <ShieldCheck size={14} /> : <AlertTriangle size={14} />}
                </span>
                <span className={`text-[11px] uppercase tracking-[0.22em] font-bold ${pdfCheck.ok ? 'text-emerald-300' : 'text-amber-300'}`}>
                  {pdfCheck.ok ? 'PDF size matches' : 'PDF size differs'}
                </span>
              </div>
              <div className={`mt-1.5 ${mono} ${subtle}`}>
                expected&nbsp;{pdfCheck.expectedBytes?.toLocaleString()} B · actual&nbsp;{pdfCheck.actualBytes?.toLocaleString()} B
              </div>
              {pdfCheck.sha256 && (
                <div className={`${mono} ${subtle}`}>
                  pdf sha256&nbsp;<span className={isDark ? 'text-white/65' : 'text-slate-700'}>{pdfCheck.sha256}</span>
                </div>
              )}
              {!pdfCheck.ok && !pdfCheck.error && (
                <div className={`mt-1.5 text-[10px] italic ${subtle}`}>
                  A size delta usually means the pack was regenerated after you saved your copy.
                  Re-download from &ldquo;Trust Pack PDF&rdquo; above and re-verify.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
